import type { ReactElement } from 'react';

import { Skeleton } from '@/components/ui/skeleton';

/**
 * Per-route skeleton for `/` (signed-in home). Mirrors the new layout
 * 1:1 — slim utility row → announcement banner placeholder → 12-col
 * grid with main column (greeting / continue watching / recently
 * listened / stats tiles / anime + manga lists) and sidebar (today's
 * schedule / news).
 *
 * No internal `DelayedSkeleton` — the parent `RouteSkeleton` already
 * wraps the returned element in one.
 *
 * Plain `<div>` (not `<main>`) outer so this composes cleanly inside
 * MainLayout's `<main>` without nesting landmarks.
 */
const CONTINUE_KEYS = ['cw-1', 'cw-2', 'cw-3', 'cw-4'] as const;
const STAT_KEYS = ['stat-1', 'stat-2', 'stat-3', 'stat-4'] as const;
const ANIME_POSTER_KEYS = [
  'ap-1',
  'ap-2',
  'ap-3',
  'ap-4',
  'ap-5',
  'ap-6',
] as const;
const SCHEDULE_KEYS = ['sc-1', 'sc-2', 'sc-3', 'sc-4'] as const;
const NEWS_THUMB_KEYS = ['nt-1', 'nt-2', 'nt-3', 'nt-4'] as const;

function GreetingSkeleton(): ReactElement {
  return (
    <div className="space-y-2">
      <Skeleton className="h-6 w-64 max-w-full" />
      <Skeleton className="h-4 w-48 max-w-full" />
    </div>
  );
}

function ContinueWatchingSkeleton(): ReactElement {
  return (
    <section>
      <Skeleton className="mb-3 h-5 w-44" />
      <div className="flex gap-3 overflow-hidden">
        {CONTINUE_KEYS.map((key) => (
          <Skeleton
            key={key}
            className="aspect-video w-[260px] flex-shrink-0 rounded-lg sm:w-[300px]"
          />
        ))}
      </div>
    </section>
  );
}

function RecentlyListenedSkeleton(): ReactElement {
  return (
    <section>
      <div className="mb-3 flex items-center justify-between">
        <Skeleton className="h-5 w-40" />
        <Skeleton className="h-7 w-20 rounded-full" />
      </div>
      <div className="space-y-2">
        <Skeleton className="h-16 w-full rounded-lg" />
        <Skeleton className="h-16 w-full rounded-lg" />
      </div>
    </section>
  );
}

function StatsTilesSkeleton(): ReactElement {
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
      {STAT_KEYS.map((key) => (
        <div
          key={key}
          className="flex items-center gap-3 rounded-2xl border border-border/70 bg-card/80 p-3 shadow-sm md:p-4"
        >
          <Skeleton className="h-10 w-10 flex-shrink-0 rounded-xl md:h-11 md:w-11" />
          <div className="min-w-0 flex-1 space-y-1.5">
            <Skeleton className="h-2.5 w-16" />
            <Skeleton className="h-5 w-12" />
          </div>
        </div>
      ))}
    </div>
  );
}

function AnimeListSkeleton(): ReactElement {
  return (
    <section>
      <div className="mb-3 flex items-center justify-between">
        <Skeleton className="h-5 w-36" />
        <Skeleton className="h-4 w-16" />
      </div>
      <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-6">
        {ANIME_POSTER_KEYS.map((key) => (
          <div key={key} className="space-y-2">
            <Skeleton className="aspect-[2/3] w-full rounded-lg" />
            <Skeleton className="h-3 w-5/6" />
            <Skeleton className="h-3 w-3/5" />
          </div>
        ))}
      </div>
    </section>
  );
}

function ScheduleSkeleton(): ReactElement {
  return (
    <section className="rounded-2xl border border-border/60 bg-card/60 p-4">
      <div className="mb-3 flex items-center justify-between">
        <Skeleton className="h-5 w-36" />
        <Skeleton className="h-4 w-12" />
      </div>
      <div className="space-y-2">
        {SCHEDULE_KEYS.map((key) => (
          <div
            key={key}
            className="flex items-center gap-3 rounded-lg border border-border/40 p-2"
          >
            <Skeleton className="h-12 w-9 flex-shrink-0 rounded" />
            <div className="min-w-0 flex-1 space-y-1.5">
              <Skeleton className="h-3 w-4/5" />
              <Skeleton className="h-2.5 w-1/2" />
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function NewsSkeleton(): ReactElement {
  return (
    <section className="rounded-2xl border border-border/60 bg-card/60 p-4">
      <div className="mb-3 flex items-center justify-between">
        <Skeleton className="h-5 w-20" />
        <Skeleton className="h-4 w-12" />
      </div>
      <div className="space-y-2">
        <Skeleton className="aspect-[16/10] w-full rounded-xl" />
        <div className="grid grid-cols-2 gap-2">
          {NEWS_THUMB_KEYS.map((key) => (
            <Skeleton key={key} className="aspect-video w-full rounded-lg" />
          ))}
        </div>
      </div>
    </section>
  );
}

function HomeMainSkeleton(): ReactElement {
  return (
    <div className="space-y-5 lg:col-span-8 xl:col-span-9">
      <GreetingSkeleton />
      <ContinueWatchingSkeleton />
      <RecentlyListenedSkeleton />
      <StatsTilesSkeleton />
      <AnimeListSkeleton />
    </div>
  );
}

function HomeSidebarSkeleton(): ReactElement {
  return (
    <div className="space-y-5 lg:col-span-4 xl:col-span-3">
      <ScheduleSkeleton />
      <NewsSkeleton />
    </div>
  );
}

export function HomeSkeleton(): ReactElement {
  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto w-full max-w-[1800px] px-4 py-5 sm:px-6 lg:px-8 2xl:px-12">
        <div className="mb-4 flex items-center justify-end gap-2">
          <Skeleton className="h-6 w-24 rounded-full" />
          <Skeleton className="h-6 w-32 rounded-full" />
        </div>
        <Skeleton className="mb-5 h-14 w-full rounded-2xl" />
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-12 xl:gap-8 2xl:gap-10">
          <HomeMainSkeleton />
          <HomeSidebarSkeleton />
        </div>
      </div>
    </div>
  );
}

export default HomeSkeleton;
